import { messageInfo } from 'reusable/mui/modals/message-info'
import { isArray } from 'reusable/utils/array-utils'
import { messageActions } from 'reusable/mui/modals/message-actions'
import { messageConfirm } from 'reusable/mui/modals/message-confirm'
import Box from '@mui/material/Box'
import { themeConfig } from '@gap/shared/theme'
import { DialogButtonOptions } from 'reusable/gap-dialog/GapDialogActionButton'
import Debug from 'reusable/utils/debug'

export interface fetchWithErrorHandlingProps {
   /**
    * inline function that will fetch data and return a Response
    */
   fetchCall: () => Promise<Response> | Response
   /**
    * doesn't display any messages
    */
   silent?: boolean
   /**
    * @default 'promise-reject'
    */
   errorThrowType?: 'promise-reject' | 'throw'
   /**
    * option array that will contain property names from a json error that can be used as a error message content
    */
   errorMessageContentProperties?: string[]
   /**
    * option array that will contain property names from a json error that can be used as a error message title
    */
   errorMessageTitleProperties?: string[]
   /**
    * option array that will contain property names from a json error that can be used as a error message stack trace
    */
   errorMessageStackTraceProperties?: string[]
   /**
    * option array that will contain property names from a json error that can be used as a error message full error object
    */
   errorMessageFullErrorProperties?: string[]
   /**
    * function that will be called when the api returned any status different than 2** and 401 (error) so you can
    * handle this error yourself an possibly access custom properties from errorResponseObjectFromJson
    * @param errorResponseObjectFromJson error json object returned by the server
    * @param response http response object
    * @returns if you return true, you are requesting that no UI is to be shown with the error (because you might show it in your code, or it is not necessary at all)
    */
   serverErrorCustomHandling?: (
      errorResponseObjectFromJson: any,
      response?: Response
   ) => Promise<boolean | undefined>
   /**
    * function that will be called when the api call throws any other error not handled by serverErrorCustomHandling. This might be for any reason.
    * @returns if you return true, you are requesting that no UI is to be shown with the error (because you might show it in your code, or it is not necessary at all)
    */
   errorCustomHandling?: (errorReason: any) => Promise<boolean | undefined>
}

type TError = {
   content: any, title?: string, stackTrace?: string, fullError?: any
}
/**
 * Use this to call apis with proper error handling and visual feedback
 * @param param0 props in fetchWithErrorHandlingProps
 * @example
 * const response = await fetchWithErrorHandling(() => window.fetch(url, options))
 */
export async function fetchWithErrorHandling({
   fetchCall,
   silent,
   errorThrowType = 'promise-reject',
   serverErrorCustomHandling,
   errorMessageContentProperties,
   errorMessageTitleProperties,
   errorMessageStackTraceProperties,
   errorMessageFullErrorProperties,
}: fetchWithErrorHandlingProps): Promise<Response> {

   const traverseObject = (obj: any, propertyName: string): any => {
      const [first, ...remaining] = propertyName.split('.')

      const value = obj?.[first]

      if (!!value && remaining.length > 0) {
         return traverseObject(value, remaining.join('.'))
      }

      return value
   }

   function getFirstValidProperty(obj: any, propertyNames?: string[]) {
      if (!propertyNames) {
         return undefined
      }
      for (const property of propertyNames) {
         const value = traverseObject(obj, property)
         if (value) {
            return value
         }
      }
   }

   const throwError = (error: any) => {
      switch (errorThrowType) {
         case 'promise-reject':
            return Promise.reject(error)
            break;

         case 'throw':
            if (typeof error === 'string') {
               throw Error(error, {})
            } else if (error instanceof Error) {
               throw error
            } else {
               throw Error(error.message, { cause: error?.cause ?? error })
            }
            break;
      }

   }

   const errorHandler = (error: TError) => {
      let { content } = error
      const { fullError, stackTrace, title } = error

      if (!content) {
         content = 'Unexpected behavior from application server.'
      }
      const contentMessage = computeErrorMessage(content)

      if (!silent) {
         const actions: DialogButtonOptions = {}

         if (fullError) {
            actions.primary = {
               name: 'Full Error',
               callback: () =>
                  messageInfo(<Box sx={{ fontWeight: 400, color: themeConfig.primary[900], p: '5px' }}>
                     <pre style={{ borderRadius: '8px' }}>
                        {JSON.stringify(fullError, null, 2)}
                     </pre>
                  </Box>, 'Full Error', {
                     primary: {
                        name: 'Close'
                     },
                     size: 'large'
                  }),
               notCloseAutomatically: true,
            }
         }

         if (stackTrace) {
            const stackTraceAction = {
               name: 'Stack Trace',
               callback: () => messageInfo(stackTrace, 'Stack Trace', { size: 'large' }),
               notCloseAutomatically: true,
            }
            if (actions.primary) {
               actions.secondary = stackTraceAction
            } else {
               actions.primary = stackTraceAction
            }
         }

         if (actions.secondary) {
            actions.cancel = { name: 'Ok' }
         } else if (actions.primary) {
            actions.secondary = { name: 'Ok' }
         } else {
            actions.primary = { name: 'Ok' }
         }

         messageActions({
            children: contentMessage,
            title,
            ...actions,
         })
      }

      return throwError(error.fullError)
   }

   const notAuthenticatedError = (additionalMsg?: string) => {
      if (!silent) {
         messageConfirm(
            `User is not authenticated${!!additionalMsg ? ` (error: ${additionalMsg})` : ''}. Would you like to reload the page?`,
            'Authentication')
            .then(confirmed => {
               if (confirmed) {
                  window.location.reload()
               }
            })
      }

      return throwError('User is not authenticated')
   }

   const errorResponse: TError = {
      content: "Error",
   }

   try {
      const response = await fetchCall()

      if (response) {
         if (response.status === 204) {
            // There's no content with a 204 response, so although it's classed as successful,
            // there is no text that comes back on the reponse object, so we just create a new
            // Response.
            return new Response()
         } else if (response.status === 401) {
            return notAuthenticatedError()
         } else if (response.status.toString()[0] !== '2') {
            try {
               const textResponse = await response.text()

               try {
                  let jsonResponse: any
                  try {
                     jsonResponse = !!textResponse ? JSON.parse(textResponse) : ''
                  } catch {
                     jsonResponse = textResponse ?? ''
                  }

                  if (
                     serverErrorCustomHandling &&
                     (await serverErrorCustomHandling(jsonResponse, response)) === true
                  ) {
                     return throwError(jsonResponse)
                  }

                  if (isArray(jsonResponse)) {
                     jsonResponse = jsonResponse[0]
                  }

                  errorResponse.content =
                     getFirstValidProperty(
                        jsonResponse,
                        errorMessageContentProperties || ['error.message']
                     ) || textResponse

                  errorResponse.title =
                     getFirstValidProperty(
                        jsonResponse,
                        errorMessageTitleProperties || ['title']
                     ) || 'Error'

                  errorResponse.stackTrace = getFirstValidProperty(
                     jsonResponse,
                     errorMessageStackTraceProperties || ['error.data.stack']
                  )
                  errorResponse.fullError = getFirstValidProperty(
                     jsonResponse,
                     errorMessageFullErrorProperties || ['error']
                  )


               } catch (error) {
                  errorResponse.content = computeErrorMessage(error)
               }
            } catch (error) {
               errorResponse.content = computeErrorMessage(error)
            }

         } else {
            return response
         }
      } else {
         return response
      }

   } catch (errorCaught) {
      Debug.log.api("API Error", errorCaught)
      const errResponse: any = errorCaught
      if (serverErrorCustomHandling && (await serverErrorCustomHandling(errResponse)) === true) {
         return throwError(errResponse)
      }

      if (!!errResponse) {
         if (!!errResponse.response && errResponse.response.status === '401') {
            return notAuthenticatedError()
         }
         if (errResponse.msg === 'interaction_required' && !!errResponse.message) {
            return notAuthenticatedError(errResponse.message)
         }
      }
      errorResponse.content = computeErrorMessage(errResponse)
      errorResponse.title = 'Server Communication Error'

   }
   return errorHandler(errorResponse)
}

export const computeErrorMessage = (error: any) => {

   if (error instanceof Error) {
      return (error.stack) ?? error.message
   } else if (typeof error === 'object') {
      if (!!error.message) {
         return error.message
      }
      return JSON.stringify(error).replace(/(?:\\[rn])+/g, '\n');
   } else {
      return error
   }
}
